import type { MenuItem } from "primevue/menuitem";

/** Item de menu PrimeVue portant ses droits d'accès (sémantique OU, comme `v-access`). */
export type PermissionMenuItem = MenuItem & {
  permissions?: string[] | null;
  roles?: string[] | null;
  items?: PermissionMenuItem[];
};

/** Helpers du module nuxt-permissions (`$hasPermission`, `$hasRole`). */
export type MenuAccessCheckers = {
  hasPermission: (permissions: string | string[]) => boolean;
  hasRole: (roles: string | string[]) => boolean;
};

function isItemAccessible(item: PermissionMenuItem, checkers: MenuAccessCheckers): boolean {
  if (!item.permissions && !item.roles) return true;
  if (item.permissions && checkers.hasPermission(item.permissions)) return true;
  return Boolean(item.roles && checkers.hasRole(item.roles));
}

/**
 * Filtre récursivement un modèle de menu PrimeVue selon les droits de l'utilisateur.
 * Un groupe dont tous les enfants sont masqués est retiré (pas d'en-tête orphelin).
 * Le tableau d'origine n'est pas modifié.
 *
 * On filtre le TABLEAU plutôt que de poser `v-can` dans le slot `#item` : la directive
 * masquerait le contenu mais laisserait le `<li>` et l'en-tête de groupe PrimeVue.
 * Le masquage est un confort d'interface — la sécurité réelle reste côté API.
 */
export function filterMenuItemsByAccess(
  items: PermissionMenuItem[],
  checkers: MenuAccessCheckers,
): PermissionMenuItem[] {
  return items.reduce<PermissionMenuItem[]>((visibleItems, item) => {
    if (!isItemAccessible(item, checkers)) return visibleItems;

    if (!item.items) {
      visibleItems.push(item);
      return visibleItems;
    }

    const visibleChildren = filterMenuItemsByAccess(item.items, checkers);
    if (item.items.length > 0 && visibleChildren.length === 0) return visibleItems;

    visibleItems.push({ ...item, items: visibleChildren });
    return visibleItems;
  }, []);
}
